import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { getCurrentSessionUID } from '@selectors/quizTaking';
import { submitBulkAnswers } from '@actions/quizTaking';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import usePreciseTimer from '@hooks/usePreciseTimer';
import { BULK_SUBMIT_API_FAILED } from '@constants/segment';
import { failedSubmitAPIQueue } from '../../utils';
import useQuizSegmentAnalytics from '../../Common/useQuizSegmentAnalytics';

const useFailedAPIQueue = () => {
  const dispatch = useDispatch();
  const { query } = useRouter();
  const { quizUID } = query;

  const currentSessionUID = useSelectorWithShallowEqual(getCurrentSessionUID);
  const [isProcessing, setIsProcessing] = useState(false);

  const segmentAnalytics = useQuizSegmentAnalytics();

  const addFailedSubmitAPI = useCallback(
    (data) => failedSubmitAPIQueue.add(data),
    []
  );

  const sendFailedAPIEvents = (data) => {
    segmentAnalytics(BULK_SUBMIT_API_FAILED, data);
  };

  const processFailedSubmitAPIS = useCallback(
    async ({ shouldRetry = true }) => {
      const failedItems = failedSubmitAPIQueue.get({
        items: failedSubmitAPIQueue.count()
      });

      if (failedItems.length) {
        const resp = await dispatch(
          submitBulkAnswers(quizUID, currentSessionUID, failedItems)
        );

        sendFailedAPIEvents({
          failed_items: failedItems.length,
          quiz_session_id: currentSessionUID,
          is_gonna_retry: shouldRetry
        });

        if (resp.error && shouldRetry) {
          addFailedSubmitAPI(failedItems);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentSessionUID, quizUID]
  );

  const startProcessingFailedAPIS = useCallback(() => {
    setIsProcessing(true);
  }, []);

  const forceProcessAPISImmediately = useCallback(async () => {
    await processFailedSubmitAPIS({ shouldRetry: false });
  }, [processFailedSubmitAPIS]);

  usePreciseTimer(processFailedSubmitAPIS, 120000, isProcessing);

  return {
    addFailedSubmitAPI,
    forceProcessAPISImmediately,
    startProcessingFailedAPIS
  };
};

export default useFailedAPIQueue;
